import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/components/GuidedTour";
import { hasPermission, isSuperuser, inScope } from "@/lib/permissions";
import { getActivation, markActivation, syncActivationFromServer } from "@/lib/activation";
import { CheckCircle2, Circle, Compass, ListChecks, Network, Radar, ShieldCheck, Telescope, X } from "lucide-react";

const DISMISS_KEY = "erio_activation_dismissed_v1";
const TOUR_DONE_KEY = "erio_tour_done_v1";

// Role-aware step set — mirrors the same access gates as the guided tour & routes.
function buildItems(user, startTour, navigate) {
  const sup = isSuperuser(user);
  const can = (p) => hasPermission(user, p);
  const isAdminLike = ["org_admin", "admin"].includes(user?.role) || sup;
  const items = [
    { key: "tour", icon: Compass, label: "Take the 60-second guided tour", action: startTour, cta: "Start tour" },
  ];
  if (can("risk.read"))
    items.push({ key: "risks", icon: ListChecks, label: "Open your risk register", action: () => navigate("/risks"), cta: "Go" });
  if (can("risk.read"))
    items.push({ key: "foresight", icon: Telescope, label: "Run a Foresight projection", action: () => navigate("/dashboard"), cta: "Open", hint: "Dashboard → Foresight tab" });
  if (can("supplier.read") && inScope(user, "t_thirdparty"))
    items.push({ key: "suppliers", icon: Network, label: "Explore the supply-chain graph", action: () => navigate("/suppliers"), cta: "Go" });
  if (can("signal.read"))
    items.push({ key: "signals", icon: Radar, label: "Review live intelligence signals", action: () => navigate("/signals"), cta: "Go" });
  if (can("attestation.read"))
    items.push({ key: "governance", icon: ShieldCheck, label: sup ? "Visit the Trust Centre" : "Review attestations & evidence", action: () => navigate("/governance"), cta: "Go" });

  // Admin-only setup tasks — surfaces the activation work that org-admins
  // need to do once for the whole tenant. These don't appear for viewers/
  // contributors who can't action them.
  if (isAdminLike) {
    items.push({
      key: "admin_add_user",
      icon: Compass,
      label: "Invite your first colleague to the tenant",
      action: () => navigate("/admin?tab=users"),
      cta: "Open",
      hint: "Admin → Users",
    });
    items.push({
      key: "admin_appetite",
      icon: Compass,
      label: "Set risk appetite for each theme",
      action: () => navigate("/appetite"),
      cta: "Open",
    });
    items.push({
      key: "admin_connector",
      icon: Compass,
      label: "Connect your first PPM source (Jira / ServiceNow / MS Project / Smartsheet)",
      action: () => navigate("/connectors"),
      cta: "Open",
    });
    items.push({
      key: "admin_supplier",
      icon: Network,
      label: "Add your first supplier (or upload a BYO CSV)",
      action: () => navigate("/suppliers"),
      cta: "Open",
    });
    items.push({
      key: "admin_help",
      icon: ShieldCheck,
      label: "Read the role manual in the Help Centre",
      action: () => navigate("/help"),
      cta: "Open",
      hint: "Searchable, downloadable as PDF",
    });
  }
  return items;
}

const isDone = (key, act) => {
  if (key === "tour") {
    try { return localStorage.getItem(TOUR_DONE_KEY) === "1"; } catch { return false; }
  }
  return !!act[key];
};

export const ActivationChecklist = () => {
  const { user } = useAuth();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { setDismissed(localStorage.getItem(DISMISS_KEY) === "1"); } catch { /* ignore */ }
    syncActivationFromServer();
    const h = () => setTick((t) => t + 1);
    window.addEventListener("erio-activation", h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener("erio-activation", h);
      window.removeEventListener("storage", h);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  if (!user || dismissed) return null;

  const items = buildItems(user, startTour, navigate);
  const act = getActivation();
  const done = items.filter((i) => isDone(i.key, act)).length;
  const total = items.length;
  const pct = Math.round((done / total) * 100);
  const allDone = done === total;

  return (
    <div
      data-testid="activation-checklist"
      className="relative mb-6 overflow-hidden rounded-2xl border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.06] to-transparent p-5"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss checklist"
        data-testid="activation-dismiss"
        className="absolute right-3 top-3 rounded-md p-1 text-ek-ink/35 transition-colors hover:bg-ek-ink/5 hover:text-ek-ink"
      >
        <X size={15} />
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 pr-6">
        <div>
          <h2 className="font-display text-base font-semibold text-ek-ink">
            {allDone ? "You're all set 🎉" : "Get started with ERIO"}
          </h2>
          <p className="mt-0.5 text-xs text-ek-ink/65">
            {allDone
              ? "You've explored the core of your decision-intelligence layer. You can dismiss this anytime."
              : "A few quick steps to get the most from your decision-intelligence layer."}
          </p>
        </div>
        <div className="text-right">
          <p className="font-display text-lg font-bold text-ek-purple" data-testid="activation-progress">
            {done} / {total}
          </p>
          <p className="text-[10px] uppercase tracking-wide text-ek-ink/65">complete</p>
        </div>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ek-purple/10">
        <div className="h-full rounded-full bg-ek-purple transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {items.map((i) => {
          const complete = isDone(i.key, act);
          const Icon = i.icon;
          return (
            <li
              key={i.key}
              data-testid={`activation-item-${i.key}`}
              className={`flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5 ${complete ? "border-emerald-200" : "border-black/5"}`}
            >
              {complete ? (
                <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
              ) : (
                <Circle size={18} className="shrink-0 text-ek-ink/20" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm text-ek-ink/80">
                  <Icon size={13} className="shrink-0 text-ek-purple" />
                  <span className={complete ? "line-through decoration-emerald-400/60" : ""}>{i.label}</span>
                </div>
                {i.hint && <p className="ml-[18px] text-[11px] text-ek-ink/65">{i.hint}</p>}
              </div>
              {!complete && (
                <button
                  onClick={() => {
                    // Mark on click so admin items (which don't have route-
                    // level activation hooks) tick off when actioned. The
                    // server endpoint is idempotent so this is safe.
                    if (i.key.startsWith("admin_")) {
                      try {
                        markActivation(i.key);
                      } catch {
                        /* ignore */
                      }
                    }
                    i.action?.();
                  }}
                  data-testid={`activation-cta-${i.key}`}
                  className="shrink-0 rounded-lg bg-ek-purple/10 px-2.5 py-1 text-xs font-semibold text-ek-purple transition-colors hover:bg-ek-purple hover:text-white"
                >
                  {i.cta}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
